import React, { useEffect, useRef, useState } from 'react';
import { useT } from './LanguageProvider';
import { LocaleCode } from './config';

interface Props {
	/** 'sidebar' = full-width row (app nav); 'compact' = small pill (recipient view). */
	variant?: 'sidebar' | 'compact';
	className?: string;
}

const GlobeIcon = ({ className = 'w-4 h-4' }: { className?: string }) => (
	<svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
		<path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
		<path strokeLinecap="round" strokeLinejoin="round" d="M3.6 9h16.8M3.6 15h16.8M12 3a15 15 0 010 18M12 3a15 15 0 000 18" />
	</svg>
);

const LanguageSwitcher: React.FC<Props> = ({ variant = 'compact', className = '' }) => {
	const { locale, languages, setLocale, dir } = useT();
	const [open, setOpen] = useState(false);
	const ref = useRef<HTMLDivElement>(null);
	const active = languages.find((l) => l.code === locale);

	useEffect(() => {
		const onClick = (e: MouseEvent) => {
			if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
		};
		document.addEventListener('mousedown', onClick);
		return () => document.removeEventListener('mousedown', onClick);
	}, []);

	// The compact pill sits at the top-left of the page, so its menu must extend
	// rightward (left-0) to stay on screen. The sidebar variant aligns by text dir.
	const menuAlign = variant === 'compact' ? 'left-0' : dir === 'rtl' ? 'left-0' : 'right-0';
	// Sidebar sits at the bottom of the nav → open upward; the compact pill sits at
	// the top of the page → open downward so the menu stays on-screen.
	const menuVertical = variant === 'sidebar' ? 'bottom-full mb-2' : 'top-full mt-2';

	return (
		<div ref={ref} className={`relative inline-block ${className}`}>
			<button
				onClick={() => setOpen((o) => !o)}
				aria-label="Change language"
				className={
					variant === 'sidebar'
						? 'flex items-center gap-2 w-full px-3 py-2 rounded-lg text-sm font-medium text-white/90 hover:bg-white/10 transition'
						: 'flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium bg-white/90 backdrop-blur-sm text-slate-700 border border-slate-200 shadow-sm hover:bg-white transition'
				}
			>
				<GlobeIcon />
				<span>{active?.nativeLabel || 'Language'}</span>
				<svg className={`w-3 h-3 transition-transform ${open ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
					<path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
				</svg>
			</button>
			{open && (
				<div className={`absolute ${menuAlign} ${menuVertical} min-w-[160px] bg-white rounded-xl shadow-xl border border-slate-100 overflow-hidden z-[60]`}>
					{languages.map((l) => (
						<button
							key={l.code}
							onClick={() => {
								setLocale(l.code as LocaleCode);
								setOpen(false);
							}}
							className={`flex items-center justify-between w-full px-4 py-2.5 text-sm text-left hover:bg-slate-50 transition ${
								l.code === locale ? 'text-blue-700 font-semibold bg-blue-50' : 'text-slate-700'
							}`}
						>
							<span>{l.nativeLabel}</span>
							{l.code === locale && (
								<svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2.5">
									<path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
								</svg>
							)}
						</button>
					))}
				</div>
			)}
		</div>
	);
};

export default LanguageSwitcher;
